// Placeholder data for the Overview page. Structure/shape only — every value
// here is illustrative, standing in until this page is wired to real public
// endpoints (see services/publicApiClient.js).

import { STATE_FILTER_OPTIONS } from './nigeriaStates';

// Grid position (not real geography) for the state-tile cartogram, plus a
// mock population figure the tile's sequential shade is computed from —
// see NigeriaLivestockOverview.jsx's `shadeForPopulation`.
export const NIGERIA_STATES_GRID = [
  { name: 'Abia', row: 1, col: 1, population: 1_900_000 },
  { name: 'Adamawa', row: 1, col: 2, population: 2_400_000 },
  { name: 'Akwa Ibom', row: 1, col: 3, population: 1_600_000 },
  { name: 'Anambra', row: 1, col: 4, population: 2_100_000 },
  { name: 'Bauchi', row: 1, col: 5, population: 3_100_000 },
  { name: 'Bayelsa', row: 1, col: 6, population: 900_000 },
  { name: 'Benue', row: 1, col: 7, population: 3_400_000 },

  { name: 'Borno', row: 2, col: 1, population: 2_800_000 },
  { name: 'Cross River', row: 2, col: 2, population: 1_700_000 },
  { name: 'Delta', row: 2, col: 3, population: 2_000_000 },
  { name: 'Ebonyi', row: 2, col: 4, population: 1_500_000 },
  { name: 'Edo', row: 2, col: 5, population: 1_900_000 },
  { name: 'Ekiti', row: 2, col: 6, population: 1_100_000 },

  { name: 'Enugu', row: 3, col: 3, population: 1_800_000 },
  { name: 'Gombe', row: 3, col: 4, population: 2_200_000 },
  { name: 'Imo', row: 3, col: 5, population: 1_600_000 },
  { name: 'Jigawa', row: 3, col: 6, population: 2_900_000 },
  { name: 'Kaduna', row: 3, col: 7, population: 3_600_000 },

  { name: 'Kano', row: 4, col: 1, population: 3_900_000 },
  { name: 'Katsina', row: 4, col: 2, population: 3_300_000 },
  { name: 'Kebbi', row: 4, col: 3, population: 2_600_000 },
  { name: 'Kogi', row: 4, col: 4, population: 1_800_000 },
  { name: 'Kwara', row: 4, col: 5, population: 1_500_000 },

  { name: 'Lagos', row: 5, col: 1, population: 1_100_000 },
  { name: 'Nasarawa', row: 5, col: 2, population: 2_300_000 },
  { name: 'Niger', row: 5, col: 3, population: 3_200_000 },
  { name: 'Ogun', row: 5, col: 4, population: 1_400_000 },
  { name: 'Ondo', row: 5, col: 5, population: 1_600_000 },
  { name: 'Osun', row: 5, col: 6, population: 1_200_000 },

  { name: 'Oyo', row: 6, col: 1, population: 2_500_000 },
  { name: 'Plateau', row: 6, col: 2, population: 2_700_000 },
  { name: 'Rivers', row: 6, col: 3, population: 1_300_000 },
  { name: 'Sokoto', row: 6, col: 4, population: 3_000_000 },
  { name: 'Taraba', row: 6, col: 5, population: 2_600_000 },
  { name: 'Yobe', row: 6, col: 6, population: 2_400_000 },
  { name: 'Zamfara', row: 6, col: 7, population: 2_200_000 },

  { name: 'Federal Capital Territory', row: 7, col: 3, population: 800_000 },
];

export const DEFAULT_SELECTED_STATE = 'Kaduna';

// Per-state detail panel content. Kaduna mirrors the reference design; every
// other state derives plausible figures from its own mock population so the
// whole grid is clickable without 37 hand-authored records.
const KNOWN_STATE_DETAILS = {
  Kaduna: {
    livestockMarkets: 250,
    registeredFacilities: 20,
    registeredVeterinarians: 150,
    coordinator: 'Sani Aliyu',
    coordinatorPhone: '+2347075576890',
  },
};

export function getStateDetails(stateName) {
  const tile = NIGERIA_STATES_GRID.find((state) => state.name === stateName);
  const population = tile?.population ?? 0;
  const known = KNOWN_STATE_DETAILS[stateName];

  return {
    name: stateName,
    livestockPopulation: population,
    livestockMarkets: known?.livestockMarkets ?? Math.max(20, Math.round(population / 15_000)),
    registeredFacilities: known?.registeredFacilities ?? Math.max(2, Math.round(population / 180_000)),
    registeredVeterinarians: known?.registeredVeterinarians ?? Math.max(10, Math.round(population / 24_000)),
    coordinator: known?.coordinator ?? 'Not yet assigned',
    coordinatorPhone: known?.coordinatorPhone ?? '—',
  };
}

// Legend order is fixed (categorical identity) — never re-sorted by value.
// Colors are assigned from the palette's validated adjacent-safe order for
// line charts (blue, orange, aqua, yellow, magenta).
export const MARKET_PRICE_SPECIES = [
  { key: 'cattle', label: 'Cattle', color: '#2a78d6' },
  { key: 'goat', label: 'Goat', color: '#eb6834' },
  { key: 'chicken', label: 'Chicken', color: '#1baf7a' },
  { key: 'sheep', label: 'Sheep', color: '#eda100' },
  { key: 'microlivestock', label: 'Microlivestock', color: '#e87ba4' },
];

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const BASE_PRICE = { cattle: 400000, goat: 115000, chicken: 4400, sheep: 95000, microlivestock: 34000 };

const daysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

// State/market pairing for the Overview page's market-prices chart filter.
// Placeholder markets per state — swap for a real market-prices endpoint
// later; only this map (and the location multiplier below) needs to change.
export const MARKET_PRICE_STATE_MARKETS = {
  Kaduna: ['Kasuwan Shanu', 'Kaduna Central Market', 'Saminaka Market'],
  Kano: ['Wurno Market', 'Dawanau Market', 'Kurmi Market'],
  Lagos: ['Ariaria Market', 'Oko-oba Market', 'Agege Market'],
  Oyo: ['Bodija Market', 'Akesan Market', 'Sango Market'],
};

export const MARKET_PRICE_STATES = STATE_FILTER_OPTIONS;

// Markets available for a given state filter — 'All Markets' plus that
// state's own markets, or every market (deduped) when no state is selected.
export function getMarketsForState(state) {
  if (!state || state === 'All States') {
    return ['All Markets', ...new Set(Object.values(MARKET_PRICE_STATE_MARKETS).flat())];
  }
  return ['All Markets', ...(MARKET_PRICE_STATE_MARKETS[state] ?? [])];
}

// Deterministic (not random) multiplier derived from a state/market label so
// each selection reads as its own distinct price level instead of a copy of
// the same series.
function locationMultiplier(seedKey) {
  if (!seedKey) return 1;
  let hash = 0;
  for (let i = 0; i < seedKey.length; i += 1) hash = (hash * 31 + seedKey.charCodeAt(i)) >>> 0;
  return 0.82 + (hash % 36) / 100;
}

// Deterministic (not random) mock ₦ average price for one species/day — a
// smooth seasonal wave plus a small daily wobble and a slow year/month
// drift, so switching the calendar or the period toggle visibly changes the
// chart without needing a hand-authored table for every month. Swap for a
// real market-prices endpoint later; only this function needs to change.
function dailyValue(speciesKey, year, monthIndex, day, seedKey) {
  const base = BASE_PRICE[speciesKey];
  const seasonal = Math.sin((monthIndex + day / 30) * 0.6) * 0.06;
  const wobble = Math.sin(day * 1.4 + monthIndex * 2) * 0.025;
  const yearDrift = (year - 2025) * 0.015;
  const monthDrift = (monthIndex - 5) * 0.01;
  const locationMult = locationMultiplier(seedKey ? `${seedKey}:${speciesKey}` : '');
  return Math.max(0, Math.round(base * locationMult * (1 + seasonal + wobble + yearDrift + monthDrift)));
}

function seriesSeedKey(state, market) {
  const hasState = state && state !== 'All States';
  const hasMarket = market && market !== 'All Markets';
  if (!hasState && !hasMarket) return '';
  return `${hasState ? state : 'All States'}|${hasMarket ? market : 'All Markets'}`;
}

// Daily view: one point per day of the given month, optionally scoped to a
// state and/or market selection.
export function getDailySeries(year, monthIndex, state, market) {
  const seedKey = seriesSeedKey(state, market);
  const total = daysInMonth(year, monthIndex);
  return Array.from({ length: total }, (_, index) => {
    const day = index + 1;
    const point = { label: String(day) };
    MARKET_PRICE_SPECIES.forEach((species) => {
      point[species.key] = dailyValue(species.key, year, monthIndex, day, seedKey);
    });
    return point;
  });
}

// Weekly view: the given month's days averaged into 7-day buckets.
export function getWeeklySeries(year, monthIndex, state, market) {
  const daily = getDailySeries(year, monthIndex, state, market);
  const weeks = [];
  for (let start = 0; start < daily.length; start += 7) {
    const chunk = daily.slice(start, start + 7);
    const point = { label: `Week ${weeks.length + 1}` };
    MARKET_PRICE_SPECIES.forEach((species) => {
      const avg = chunk.reduce((sum, day) => sum + day[species.key], 0) / chunk.length;
      point[species.key] = Math.round(avg);
    });
    weeks.push(point);
  }
  return weeks;
}

// Monthly view: the given year's 12 months, each averaged from its own daily series.
export function getMonthlySeries(year, state, market) {
  return MONTH_SHORT.map((label, monthIndex) => {
    const daily = getDailySeries(year, monthIndex, state, market);
    const point = { label };
    MARKET_PRICE_SPECIES.forEach((species) => {
      const avg = daily.reduce((sum, day) => sum + day[species.key], 0) / daily.length;
      point[species.key] = Math.round(avg);
    });
    return point;
  });
}

// Legend readout for whichever series is currently on screen: latest point's
// value, and its change against the point before it.
export function summarizeSeries(series) {
  const last = series[series.length - 1];
  const previous = series[series.length - 2];
  const summary = {};

  MARKET_PRICE_SPECIES.forEach((species) => {
    const value = last?.[species.key] ?? 0;
    const previousValue = previous?.[species.key];
    const changePct = previousValue ? ((value - previousValue) / previousValue) * 100 : 0;
    summary[species.key] = { value, changePct: Math.round(changePct * 10) / 10 };
  });

  return summary;
}

// Placeholder rows for the Markets Prices directory and Overview price cards.
// Each of the 4 filter states (Kaduna, Kano, Lagos, Oyo) has a full set of
// species entries so selecting any state changes every card's data.
export const MARKET_PRICE_LISTINGS = [
  // ── All-States baseline (shown when no state is filtered) ─────────────────
  { id:  1, species: 'Cattle',   type: 'Live Animal',  unit: 'Per Head', market: 'Kano Cattle Market',    state: 'Kano',     lga: 'Kano Municipal', frequency: 'Weekly', price: 350000, source: 'FMLD' },
  { id:  2, species: 'Goat',     type: 'Live Animal',  unit: 'Per Head', market: 'Sheme Cattle Market',    state: 'Katsina',  lga: 'Katsina',        frequency: 'Daily',  price:  85000, source: 'FMLD' },
  { id:  3, species: 'Sheep',    type: 'Live Animal',  unit: 'Per Head', market: 'Maitagari Market',       state: 'Jigawa',   lga: 'Maigatari',      frequency: 'Weekly', price:  80000, source: 'FMLD' },
  { id:  4, species: 'Chicken',  type: 'Live Animal',  unit: 'Per Head', market: 'Nasarawa Abattoir',      state: 'Nasarawa', lga: 'Nasarawa',       frequency: 'Daily',  price:   8500, source: 'FMLD' },
  { id:  5, species: 'Pig',      type: 'Live Animal',  unit: 'Per Head', market: 'Ekiti Abattoir',         state: 'Ekiti',    lga: 'Ado-Ekiti',      frequency: 'Daily',  price:  67000, source: 'FMLD' },
  { id:  6, species: 'Bushmeat', type: 'Dressed Meat', unit: 'Per Kg',   market: 'Gombe Abattoir',         state: 'Gombe',    lga: 'Gombe',          frequency: 'Daily',  price:   3000, source: 'FMLD' },

  // ── Kaduna ────────────────────────────────────────────────────────────────
  { id:  7, species: 'Cattle',   type: 'Live Animal',  unit: 'Per Head', market: 'Kasuwan Shanu Market',   state: 'Kaduna',   lga: 'Kaduna North',   frequency: 'Weekly', price: 380000, source: 'FMLD' },
  { id:  8, species: 'Goat',     type: 'Live Animal',  unit: 'Per Head', market: 'Kaduna Central Market',  state: 'Kaduna',   lga: 'Kaduna South',   frequency: 'Daily',  price:  90000, source: 'FMLD' },
  { id:  9, species: 'Sheep',    type: 'Live Animal',  unit: 'Per Head', market: 'Saminaka Market',        state: 'Kaduna',   lga: 'Lere',           frequency: 'Weekly', price:  85000, source: 'FMLD' },
  { id: 10, species: 'Chicken',  type: 'Live Animal',  unit: 'Per Head', market: 'Kaduna Abattoir',        state: 'Kaduna',   lga: 'Kaduna North',   frequency: 'Daily',  price:   9500, source: 'FMLD' },
  { id: 11, species: 'Pig',      type: 'Live Animal',  unit: 'Per Head', market: 'Rigasa Market',          state: 'Kaduna',   lga: 'Igabi',          frequency: 'Daily',  price:  72000, source: 'FMLD' },
  { id: 12, species: 'Bushmeat', type: 'Dressed Meat', unit: 'Per Kg',   market: 'Malali Market',          state: 'Kaduna',   lga: 'Kaduna North',   frequency: 'Weekly', price:   3500, source: 'FMLD' },

  // ── Kano ─────────────────────────────────────────────────────────────────
  { id: 13, species: 'Cattle',   type: 'Live Animal',  unit: 'Per Head', market: 'Dawanau Cattle Market',  state: 'Kano',     lga: 'Doguwa',         frequency: 'Weekly', price: 365000, source: 'FMLD' },
  { id: 14, species: 'Goat',     type: 'Live Animal',  unit: 'Per Head', market: 'Kurmi Market',           state: 'Kano',     lga: 'Kano Municipal', frequency: 'Daily',  price:  95000, source: 'FMLD' },
  { id: 15, species: 'Sheep',    type: 'Live Animal',  unit: 'Per Head', market: 'Rimi Market',            state: 'Kano',     lga: 'Kano Municipal', frequency: 'Weekly', price:  88000, source: 'FMLD' },
  { id: 16, species: 'Chicken',  type: 'Live Animal',  unit: 'Per Head', market: 'Kano Central Abattoir',  state: 'Kano',     lga: 'Fagge',          frequency: 'Daily',  price:   8000, source: 'FMLD' },
  { id: 17, species: 'Pig',      type: 'Live Animal',  unit: 'Per Head', market: 'Sabon Gari Market',      state: 'Kano',     lga: 'Kano Municipal', frequency: 'Daily',  price:  65000, source: 'FMLD' },
  { id: 18, species: 'Bushmeat', type: 'Dressed Meat', unit: 'Per Kg',   market: 'Yankaba Market',         state: 'Kano',     lga: 'Nasarawa',       frequency: 'Weekly', price:   2800, source: 'FMLD' },

  // ── Lagos ─────────────────────────────────────────────────────────────────
  { id: 19, species: 'Cattle',   type: 'Live Animal',  unit: 'Per Head', market: 'Oko-oba Abattoir',       state: 'Lagos',    lga: 'Agege',          frequency: 'Daily',  price: 420000, source: 'FMLD' },
  { id: 20, species: 'Goat',     type: 'Live Animal',  unit: 'Per Head', market: 'Mile 12 Market',         state: 'Lagos',    lga: 'Kosofe',         frequency: 'Daily',  price: 115000, source: 'FMLD' },
  { id: 21, species: 'Sheep',    type: 'Live Animal',  unit: 'Per Head', market: 'Agege Market',           state: 'Lagos',    lga: 'Agege',          frequency: 'Weekly', price: 105000, source: 'FMLD' },
  { id: 22, species: 'Chicken',  type: 'Live Animal',  unit: 'Per Head', market: 'Ikeja Poultry Market',   state: 'Lagos',    lga: 'Ikeja',          frequency: 'Daily',  price:  13500, source: 'FMLD' },
  { id: 23, species: 'Pig',      type: 'Live Animal',  unit: 'Per Head', market: 'Ikeja Abattoir',         state: 'Lagos',    lga: 'Ikeja',          frequency: 'Daily',  price:  88000, source: 'FMLD' },
  { id: 24, species: 'Bushmeat', type: 'Dressed Meat', unit: 'Per Kg',   market: 'Mile 2 Market',          state: 'Lagos',    lga: 'Amuwo-Odofin',   frequency: 'Weekly', price:   4800, source: 'FMLD' },

  // ── Oyo ──────────────────────────────────────────────────────────────────
  { id: 25, species: 'Cattle',   type: 'Live Animal',  unit: 'Per Head', market: 'Bodija Market',          state: 'Oyo',      lga: 'Ibadan North',   frequency: 'Daily',  price: 395000, source: 'FMLD' },
  { id: 26, species: 'Goat',     type: 'Live Animal',  unit: 'Per Head', market: 'Akesan Market',          state: 'Oyo',      lga: 'Ibadan South',   frequency: 'Daily',  price: 105000, source: 'FMLD' },
  { id: 27, species: 'Sheep',    type: 'Live Animal',  unit: 'Per Head', market: 'Sango Market',           state: 'Oyo',      lga: 'Egbeda',         frequency: 'Weekly', price:  98000, source: 'FMLD' },
  { id: 28, species: 'Chicken',  type: 'Live Animal',  unit: 'Per Head', market: 'Ojoo Market',            state: 'Oyo',      lga: 'Ibadan North',   frequency: 'Daily',  price:  12000, source: 'FMLD' },
  { id: 29, species: 'Pig',      type: 'Live Animal',  unit: 'Per Head', market: 'Challenge Market',       state: 'Oyo',      lga: 'Ibadan North',   frequency: 'Daily',  price:  82000, source: 'FMLD' },
  { id: 30, species: 'Bushmeat', type: 'Dressed Meat', unit: 'Per Kg',   market: 'Dugbe Market',           state: 'Oyo',      lga: 'Ibadan South',   frequency: 'Weekly', price:   4200, source: 'FMLD' },
];

export const MARKET_PRICE_LISTING_STATES = STATE_FILTER_OPTIONS;

export const MARKET_PRICE_LISTINGS_UPDATED = '13 Aug. 2025';

// By-product price listings (hides, dairy, feathers, etc.)
export const BYPRODUCT_PRICE_LISTINGS = [
  { id: 1, product: 'Cattle Hide',      unit: 'Per Piece', market: 'Kano Leather Market',   state: 'Kano',     price: 4500,  source: 'FMLD' },
  { id: 2, product: 'Goat Skin',        unit: 'Per Piece', market: 'Sokoto Skin Market',    state: 'Sokoto',   price: 1800,  source: 'FMLD' },
  { id: 3, product: 'Fresh Cow Milk',   unit: 'Per Litre', market: 'Maiduguri Dairy Hub',   state: 'Borno',    price: 650,   source: 'FMLD' },
  { id: 4, product: 'Poultry Feathers', unit: 'Per Kg',    market: 'Ibadan Poultry Market', state: 'Oyo',      price: 300,   source: 'FMLD' },
  { id: 5, product: 'Bone Meal',        unit: 'Per Kg',    market: 'Kaduna Abattoir',       state: 'Kaduna',   price: 420,   source: 'FMLD' },
];

// Feed & fodder price listings
export const FEED_FODDER_LISTINGS = [
  { id: 1, product: 'Maize (Corn)',         unit: 'Per 100 Kg', market: 'Kano Grain Market',    state: 'Kano',     price: 45000, source: 'FMLD' },
  { id: 2, product: 'Groundnut Cake',       unit: 'Per 50 Kg',  market: 'Katsina Feed Market',  state: 'Katsina',  price: 22000, source: 'FMLD' },
  { id: 3, product: 'Palm Kernel Cake',     unit: 'Per 50 Kg',  market: 'Enugu Feed Hub',       state: 'Enugu',    price: 14500, source: 'FMLD' },
  { id: 4, product: 'Sorghum (Guinea Corn)', unit: 'Per 100 Kg', market: 'Gombe Grain Market',  state: 'Gombe',    price: 38000, source: 'FMLD' },
  { id: 5, product: 'Wheat Bran',           unit: 'Per 50 Kg',  market: 'Abuja Feed Depot',     state: 'Nasarawa', price: 11000, source: 'FMLD' },
];
